import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";

import FormSelectionField from "../../components/formFields/FormSelectionField";

import { STATUSARRAY } from "../../_contstants/common";
import { clearSeoTagDetails } from "./seotagSlice";
import {
  createSeoTag,
  getSeoTagByID,
  updateSeoTag,
} from "./services/seotag.services";

import FormTextArea from "../../components/formFields/FormTextArea";

const CreateSeoTag: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const SeoTagID = location.state?.SeoTagID;
  const SeoTagDetail = useAppSelector((state) => state.seotag.SeoTagDetail);
  const loading = useAppSelector((state) => state.seotag.loading);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    if (SeoTagID) {
      dispatch(getSeoTagByID(SeoTagID));
    }
    return () => {
      dispatch(clearSeoTagDetails());
    };
  }, [SeoTagID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [SeoTagDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (SeoTagID) {
        const editPayload = {
          ID: SeoTagID,
          PageName: values.PageName ? values.PageName : "",
          PageUrl: values.PageUrl ? values.PageUrl : "",
          MetaTitle: values.MetaTitle ? values.MetaTitle : "",
          MetaKeywords: values.MetaKeywords ? values.MetaKeywords : "",
          MetaDescription: values.MetaDescription ? values.MetaDescription : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateSeoTag(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate(-1);
        }
      } else {
        const insertPayload = {
          PageName: values.PageName ? values.PageName : "",
          PageUrl: values.PageUrl ? values.PageUrl : "",
          MetaTitle: values.MetaTitle ? values.MetaTitle : "",
          MetaKeywords: values.MetaKeywords ? values.MetaKeywords : "",
          MetaDescription: values.MetaDescription ? values.MetaDescription : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createSeoTag(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/seotag");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}

      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={SeoTagDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 5, cols: 10 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                <GridLayoutItem colSpan={4}>
                  <Typography.h4>
                    {SeoTagID ? "Update Seo Tag" : "Add Seo Tag"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="PageName"
                    label="Page Name"
                    placeholder="Page Name"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="PageUrl"
                    label="Page Url"
                    placeholder="Page Url"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="MetaTitle"
                    label="Meta Title "
                    placeholder="Meta Title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={4}>
                  <Field
                    name="MetaKeywords"
                    label="Meta Key Words"
                    placeholder="Meta Key words"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2} style={{ width: "100%" }}>
                  <Field
                    name="MetaDescription"
                    label="Meta Description"
                    component={FormTextArea}
                    placeholder="Meta Description"
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={SeoTagID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />

                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => navigate("/seotag")}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateSeoTag;
